import { getUserInfo, setIsLoadingPermissions } from "@payping/redux/actions";
import { ConfigProvider, theme as ConfigTheme, locales, theme as themeAnt } from "@payping/web-components";
import React, { useEffect, useState } from "react";
import { RouterProvider } from "react-router-dom";
import semver from "semver";

// import { ApmRoutes } from "@elastic/apm-rum-react";

import { publicRouter, router } from "@/router/Router";
import { useCheckUserPermissions } from "@/utils/RequireAuth";
import { useAsync } from "react-use";
import { LANGUAGES } from "@payping/utilities-shared/consts";

import { initSentry, removeLoading, useAppDispatch, useAppTranslation } from "./utils";
import { SharedUtilitiesConfig } from "@payping/utilities-shared/config";
import { dashboardPreferencesAtom } from "./core/state";
import { useImmerAtom } from "jotai-immer";
import { selectAtom } from "jotai/utils";
import { useAtom } from "jotai";

initSentry();

const { enUS, faIR } = locales;

const languageAtom = selectAtom(dashboardPreferencesAtom, (val) => val.language);
const directionAtom = selectAtom(dashboardPreferencesAtom, (val) => val.direction);
const themAtom = selectAtom(dashboardPreferencesAtom, (val) => val.theme);

const App: React.FC = () => {
  // =================== <REDIRECT USER BASE ON DASHBOARD PREFERENCE> ===================
  //
  const dispatch = useAppDispatch();
  const [, setDashboardPreference] = useImmerAtom(dashboardPreferencesAtom);

  const [language] = useAtom(languageAtom);
  const [direction] = useAtom(directionAtom);
  const [theme] = useAtom(themAtom);

  const { token } = themeAnt.useToken();

  // =================== </> ===================

  const [isLoading, setIsLoading] = useState<boolean>(true);
  // const isBrowserDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const { i18n } = useAppTranslation();

  // updateConfig

  useEffect(() => {
    SharedUtilitiesConfig.setConfig({ language, direction });
  }, [language, direction]);

  const configLocale = {
    en: enUS,
    fa: faIR,
  };
  useAsync(async () => {
    await dispatch(setIsLoadingPermissions(true));
    await dispatch(getUserInfo());
    await dispatch(setIsLoadingPermissions(false));
    setIsLoading(false);
    removeLoading();
  }, [dispatch]);

  useCheckUserPermissions();
  useEffect(() => {
    i18n.changeLanguage(language);
    // Get the new service worker from swUrl
    const swUrl = `/service-worker.js?v=${Math.random()}`;
    fetch(swUrl, {
      cache: "no-store",
      headers: { "Service-Worker": "script" },
    })
      .then((response) => {
        const contentType = response.headers.get("content-type");
        if (response.status === 404 || (contentType !== null && contentType.indexOf("javascript") === -1)) {
          // No service worker found. Probably a different app. Reload the page.
          navigator.serviceWorker.ready.then((registration) => {
            registration.unregister().then(() => {
              window.location.reload();
            });
          });
        } else {
          response.text().then((res) => {
            // This is the old version of the dashboard which belongs to the old service worker (current one)
            const currentVersion = process.env.REACT_APP_VERSION;
            // Extract the lastSupportedVersion from the new service-worker
            const supportedVersion = res.match(/console\.log\(['"]lastSupportedVersion[:'", ]*(.*?)['" ]\)/)?.[1];
            // Extract the latestVersion from the new service-worker
            const latestVersion = res.match(/console\.log\(['"]latestVersion[:'", ]*(.*?)['" ]\)/)?.[1];
            const currentVersionValid = currentVersion && semver.valid(currentVersion);
            const supportedVersionValid = supportedVersion && semver.valid(supportedVersion);
            const latestVersionValid = latestVersion && semver.valid(latestVersion);
            const hasUpdate =
              typeof currentVersionValid === "string" &&
              typeof latestVersionValid === "string" &&
              semver.lt(currentVersionValid, latestVersionValid);
            setDashboardPreference((draft) => {
              // eslint-disable-next-line no-param-reassign
              draft.appVersion = { current: currentVersion, latest: latestVersion, supported: supportedVersion };
              // eslint-disable-next-line no-param-reassign
              draft.serviceWorker.hasUpdate = hasUpdate;
            });
            const shouldForceUpdate =
              typeof currentVersionValid === "string" &&
              typeof supportedVersionValid === "string" &&
              semver.lt(currentVersionValid, supportedVersionValid);
            if (shouldForceUpdate) {
              navigator.serviceWorker.ready.then((registration) => {
                registration.unregister().then(() => {
                  window.location.reload();
                });
              });
            }
          });
        }
      })
      .catch(() => {
        console.log("No internet connection found. App is running in offline mode.");
      });
  }, [dispatch, i18n, language, setDashboardPreference]);

  // update tailwind dark
  useEffect(() => {
    // TODO: after Enable dark mode (107 ~ 110 line), unComment this line
    // if (theme === "dark" || isBrowserDark) {
    if (theme.color === "dark") {
      document.documentElement.classList.add("tw:dark");
    } else {
      document.documentElement.classList.remove("tw:dark");
    }
  }, [theme.color]);

  return (
    <ConfigProvider
      direction={direction}
      locale={configLocale[language || LANGUAGES.FARSI]}
      localeName={language}
      theme={{
        algorithm: [ConfigTheme.defaultAlgorithm, ...(theme.compact ? [ConfigTheme.compactAlgorithm] : [])],
        components: {
          Layout: { colorBgLayout: token.colorBgBase },
        },
      }}
    >
      <RouterProvider router={isLoading ? publicRouter : router} />
    </ConfigProvider>
  );
};

export default App;
